import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GlobalSearch from './GlobalSearch';

const Icon = {
  dashboard: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><rect x="3" y="3" width="8" height="8" rx="1.5"/><rect x="13" y="3" width="8" height="5" rx="1.5"/><rect x="13" y="12" width="8" height="9" rx="1.5"/><rect x="3" y="14" width="8" height="7" rx="1.5"/></svg>,
  event: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>,
  results: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1" fill="currentColor"/></svg>,
  participants: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><circle cx="9" cy="8" r="3.2"/><path d="M2.5 20c0-3.5 2.9-6 6.5-6s6.5 2.5 6.5 6"/><circle cx="17" cy="8" r="2.6"/><path d="M15.5 14.3c2.7.5 4.5 2.5 4.5 5.7"/></svg>,
  checkin: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3v3h-3zM19 14h2M14 19h2M19 19h2v2h-2z"/></svg>,
  activity: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><path d="M3 12h4l3 8 4-16 3 8h4"/></svg>,
  org: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><path d="M3 21V7l7-4 7 4v14"/><path d="M3 21h18M9 21v-6h4v6"/></svg>,
  category: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><circle cx="12" cy="12" r="9"/><path d="M12 3v9l6 3"/></svg>,
  discipline: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><path d="M12 2v20M4 12h16"/><circle cx="12" cy="12" r="9"/></svg>,
  search: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>,
  sun: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>,
  moon: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/></svg>,
};

const links = [
  { to: '/', label: 'لوحة التحكم', icon: 'dashboard', roles: null },
  { to: '/events', label: 'الفعاليات والتسجيل', icon: 'event', roles: null },
  { to: '/participants', label: 'كل المشاركين', icon: 'participants', roles: null },
  { to: '/results', label: 'نتائج الرماية', icon: 'results', roles: null },
  { to: '/checkin', label: 'بوابة الدخول (Check-in)', icon: 'checkin', roles: ['admin','reviewer'] },
  { to: '/activity', label: 'سجل الأنشطة', icon: 'activity', roles: ['admin','reviewer'] },
  { to: '/organizations', label: 'الجهات والاتحادات', icon: 'org', roles: ['admin','reviewer'] },
  { to: '/categories', label: 'فئات الاعتماد', icon: 'category', roles: ['admin','reviewer'] },
  { to: '/disciplines', label: 'مسابقات الرماية', icon: 'discipline', roles: ['admin','reviewer'] },
];

const roleLabels = { admin: 'مدير النظام', reviewer: 'مراجع', organization: 'جهة/اتحاد', participant: 'مشارك' };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('accred_theme') || 'light');
  const SearchIcon = Icon.search;
  const SunIcon = Icon.sun;
  const MoonIcon = Icon.moon;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('accred_theme', theme);
  }, [theme]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(true); }
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-block">
          <div className="brand-eyebrow">SPORTIX PLATFORM</div>
          <div className="brand-name">نظام الاعتماد</div>
        </div>
        <nav className="nav-group">
          {links.filter(l => !l.roles || l.roles.includes(user.role)).map(l => {
            const Ico = Icon[l.icon];
            return (
              <NavLink key={l.to} to={l.to} end={l.to === '/'} className={({isActive}) => isActive ? 'active' : ''}>
                <Ico />
                <span>{l.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-footer">accreditation.sportix.ae</div>
      </aside>
      <div className="main">
        <div className="topbar no-print">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => setSearchOpen(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 8, padding: '7px 12px', color: 'var(--muted)', cursor: 'pointer', fontSize: 13 }}
            >
              <SearchIcon width="15" height="15" />
              <span>بحث سريع</span>
              <span className="mono" style={{ fontSize: 10, opacity: 0.6 }}>Ctrl+K</span>
            </button>
            <button
              onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
              title="تبديل الوضع الليلي"
              style={{ background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 8, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)' }}
            >
              {theme === 'dark' ? <SunIcon width="16" height="16" /> : <MoonIcon width="16" height="16" />}
            </button>
          </div>
          <div className="who">مرحبًا، <b>{user.full_name}</b><span className="role-chip">{roleLabels[user.role] || user.role}</span>
            <button className="btn btn-outline" style={{ marginRight: 10 }} onClick={() => { logout(); navigate('/login'); }}>تسجيل الخروج</button>
          </div>
        </div>
        <div className="content">{children}</div>
      </div>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
